/**
 * Web Games: Play Together (#4486).
 *
 * The host opens one game URL for the room. Every participant who taps Play
 * loads that URL in a sandboxed frame on their own stage. Telegames carries
 * the URL and nothing else: the game supplies the multiplayer, if it has any.
 *
 * The URL rules here are the same ones the host's own share (#4482) applies,
 * moved out of web-next so signaling validates with the one function. Spec:
 * `_docs/Q4/WebGames/PlayTogether-FunctionalSpec.md`.
 */

/** The game currently open for the room, or nothing. Stored with the room and replayed on join. */
export interface WebGameStageState {
    /** The resolved URL every player loads, verbatim. */
    url: string;
    /** What the card and chips show; the hostname. */
    label: string;
    openedBySessionId: string;
    openedByAlias: string;
    openedAt: number;
    /** Server-generated identity of this opening; older stored stages may lack it. */
    stageGeneration?: string;
}

/** Client → server: the host opens `url` for everyone. Server validates and resolves it. */
export interface WebGameStageOpenPayload {
    roomId: string;
    url: string;
}

/** Client → server: the host closes the game for everyone. */
export interface WebGameStageClosePayload {
    roomId: string;
}

/** Server → room: the canonical state, on every change and to each socket on join. `null` = nothing open. */
export interface WebGameStageStatePayload {
    roomId: string;
    state: WebGameStageState | null;
    timestamp: number;
}

/**
 * Permissions the embedded game may use. Shared by the room's frame (#4482) and the admin
 * catalog's Test in frame preview (#4495), so a game is vetted under the attributes it will run with.
 *
 * `clipboard-write` (#4518) lets a game's own Copy button (an invite link) work: Chrome blocks
 * `navigator.clipboard.writeText` in a cross-origin frame without it, while the game still says "Copied".
 * Write only: a game cannot read what the host copied elsewhere.
 */
export const WEB_GAME_FRAME_ALLOW =
  'autoplay; fullscreen; gamepad; pointer-lock; xr-spatial-tracking; accelerometer; gyroscope; clipboard-write';

/**
 * `allow-same-origin` keeps a cross-origin game's own storage (saves) while the
 * sandbox still withholds top navigation: the host is clicking in the frame
 * continuously, so without the sandbox a game or an ad inside it could steer
 * the room tab away mid-broadcast on that sticky activation.
 */
export const WEB_GAME_FRAME_SANDBOX =
  'allow-scripts allow-same-origin allow-forms allow-popups allow-pointer-lock';

export const WEB_GAME_STAGE_OPEN = 'web-game-stage:open';
export const WEB_GAME_STAGE_CLOSE = 'web-game-stage:close';
export const WEB_GAME_STAGE_STATE = 'web-game-stage:state';

export const isWebGameStageState = (value: unknown): value is WebGameStageState => {
    if (!value || typeof value !== 'object') return false;
    const candidate = value as Partial<WebGameStageState>;
    return (
        typeof candidate.url === 'string' &&
        typeof candidate.label === 'string' &&
        typeof candidate.openedBySessionId === 'string' &&
        typeof candidate.openedByAlias === 'string' &&
        typeof candidate.openedAt === 'number'
    );
};

export type WebGameUrlResolution =
    | { ok: true; url: string; hostname: string }
    | { ok: false; error: string };

/**
 * Accepts what a host is likely to paste: a full URL, or a bare host like
 * `nevaland.day`. Only http(s) survives; anything else is refused with a
 * message the host can act on.
 *
 * `pageOrigin` is the room page's own origin. Two things depend on it:
 * - A game at the room's own origin is refused. With `allow-same-origin` in
 *   the sandbox, a same-origin frame has the parent's DOM and cookies and
 *   would join the room a second time from inside the stage.
 * - An `http:` game under an `https:` room is upgraded to `https:`. Chrome
 *   blocks the frame as mixed content otherwise, and the frame would be a
 *   silent black box.
 */
export const resolveWebGameUrl = (
    input: string,
    { pageOrigin }: { pageOrigin?: string } = {},
): WebGameUrlResolution => {
    const trimmed = input.trim();
    if (!trimmed) return { ok: false, error: 'Enter the game URL.' };

    const candidate = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`;
    let parsed: URL;
    try {
        parsed = new URL(candidate);
    } catch {
        return { ok: false, error: 'That is not a valid URL.' };
    }
    if (parsed.protocol !== 'https:' && parsed.protocol !== 'http:') {
        return { ok: false, error: 'Only http(s) URLs can be loaded on the stage.' };
    }
    if (!parsed.hostname || !parsed.hostname.includes('.') && parsed.hostname !== 'localhost') {
        return { ok: false, error: 'That is not a valid URL.' };
    }
    if (parsed.protocol === 'http:' && pageOrigin?.startsWith('https:')) {
        parsed.protocol = 'https:';
    }
    if (pageOrigin && parsed.origin === pageOrigin) {
        return { ok: false, error: 'The room itself cannot be loaded as a game.' };
    }
    return { ok: true, url: parsed.toString(), hostname: parsed.hostname };
};
